import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/SEO";
import { fetchArticle, coverImageSrc } from "@/lib/api";

function formatDate(dateStr) {
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    fetchArticle(slug)
      .then(setArticle)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-article-not-found" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-3xl text-[#1C1917]">This page has wandered off.</h1>
        <p className="text-[#57534E] mt-3">We couldn't find that story.</p>
        <Link to="/this-curious-life" className="inline-block mt-6 text-[#159E9A] font-medium hover:underline">
          ← Back to This Curious Life
        </Link>
      </div>
    );
  }

  if (!article) {
    return <div className="min-h-[60vh]" data-testid="blog-article-loading" />;
  }

  const cover = coverImageSrc(article);

  return (
    <article data-testid="blog-article-page" className="bg-[#FAFAF8]">
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/this-curious-life/${article.slug}`}
        image={cover}
      />
      {cover && (
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-8 md:pt-12">
          <div className="relative w-full aspect-[4/5] overflow-hidden">
            <img
              src={cover}
              alt={article.title}
              style={{ objectPosition: `center ${article.cover_image_focal_y ?? 50}%` }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6 md:px-10 pt-8 md:pt-10 pb-14 md:pb-20">
        <Link
          to="/this-curious-life"
          data-testid="blog-article-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#159E9A] mb-8 transition"
        >
          <ArrowLeft size={14} /> This Curious Life
        </Link>

        <p className="text-xs tracking-[0.22em] uppercase text-[#159E9A] mb-4">
          {article.author_name} · {formatDate(article.date)}
        </p>
        <h1
          data-testid="blog-article-title"
          className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight text-[#1C1917] mb-10"
        >
          {article.title}
        </h1>

        <div data-testid="blog-article-body" className="space-y-6 text-lg leading-relaxed text-[#3F3B37]">
          {article.body.split(/\n\s*\n/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      </div>
    </article>
  );
}
